import { Link, getRouteApi } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { ExportData } from '@realmery/realm-core';
import { buildExport } from '../export/html.ts';
import { renderMapJpegs } from '../export/mapPicture.ts';

/**
 * The realm as a PDF (docs/PARITY-STEP-6-PLAN.md section 4), as the desktop's
 * `export.PdfExporter` lays it out: the HTML export's content without entry picture
 * galleries, each map flattened into one picture with its pins, regions and names drawn
 * in. It's printed through the browser, where Save as PDF keeps it as a PDF; while this
 * page is open, printing shows the realm alone (styles.css, "Printing a sheet").
 */

const route = getRouteApi('/_app/realm/$realmKey/print');

export function RealmPrintPage() {
  const data = route.useLoaderData() as ExportData;
  const { realmKey } = route.useParams();
  const page = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  // The map pictures are drawn first (as `renderJpeg` at 1,400 pixels), then the whole layout built.
  useEffect(() => {
    let live = true;
    const urls: string[] = [];
    setReady(false);
    void renderMapJpegs(data.atlas, true, 1400).then((pictures) => {
      if (!live || !page.current) return;
      const bySlug = new Map<string, string>();
      for (const [slug, bytes] of pictures) {
        if (bytes.byteLength === 0) continue;
        const url = URL.createObjectURL(new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'image/jpeg' }));
        urls.push(url);
        bySlug.set(slug, url);
      }
      const body = document.createElement('div');
      buildExport(document, body, data, { images: false, overlay: false, mapPicture: (map) => bySlug.get(map.slug) ?? null });
      page.current.replaceChildren(...body.childNodes);
      setReady(true);
    });
    return () => {
      live = false;
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [data]);

  // Printing shows the realm alone, and the browser suggests its name for the PDF.
  useEffect(() => {
    const root = document.documentElement;
    const before = document.title;
    root.classList.add('printing-sheet');
    document.title = data.realm.name;
    return () => {
      root.classList.remove('printing-sheet');
      document.title = before;
    };
  }, [data.realm.name]);

  return (
    <div className="print-page">
      <div className="print-toolbar">
        <p className="eyebrow">
          <Link to="/realm/$realmKey" params={{ realmKey }}>
            ← Back to the realm
          </Link>
        </p>
        <div className="actions">
          <button type="button" className="primary" disabled={!ready} onClick={() => window.print()}>
            {ready ? 'Print or save as PDF…' : 'Drawing the maps…'}
          </button>
        </div>
        <p className="meta">
          To keep a PDF, choose <strong>Save as PDF</strong> as the printer. Page breaks and margins come from the print
          dialog.
        </p>
      </div>
      <div className="print-preview realm-print" ref={page} aria-label="The printed realm" role="document" aria-busy={!ready} />
    </div>
  );
}
